import {
  FlatTree,
  Button,
  makeStyles,
  Spinner,
  Tooltip,
  TreeOpenChangeEvent,
  TreeOpenChangeData,
  Input,
} from "@fluentui/react-components";
import {
  ArrowExpandAll20Regular,
  ArrowCollapseAll20Regular,
} from "@fluentui/react-icons";
import { getAncestors } from "../../services/TagService";
import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RoleTag } from "../../models/Role";
import { UserInformation } from "../../models/UserInformation";
import SimpleTreeItem from "./SimpleTreeItem";
import { FluentPeoplePicker } from "@skanska/vsa-people-picker";
import { useGraphClient } from "../../hooks/useGraphClient";
import { SkanskaColorTokens } from "../../style/colors";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  controls: {
    display: "flex",
    gap: "8px",
    borderBottom: "1px solid #ccc",
    padding: "0 8px 8px",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px",
    paddingLeft: "56px",
    borderBottom: "2px solid #ccc",
    backgroundColor: SkanskaColorTokens.color_secondary_light_gray,
  },
  headerTagSpace: {
    minWidth: "300px",
    maxWidth: "300px",
  },
  headerLabel: {
    fontWeight: "600",
    minWidth: "400px",
    maxWidth: "400px",
    marginLeft: "20px",
  },
  headerPickerLabel: {
    fontWeight: "600",
    minWidth: "350px",
    marginLeft: "16px",
  },
  tree: {
    flex: 1,
    overflowY: "auto",
    padding: "8px",
  },
  itemContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  tagTitle: {
    minWidth: "300px",
    maxWidth: "300px",
    overflow: "hidden",
  },

  peoplePickerContainer: {
    display: "flex",
    gap: "16px",
    marginLeft: "16px",
    flex: 1,
  },
  peoplePicker: {
    minWidth: "350px",
    borderRadius: "0px !important",
    "& .selected-list-item": {
      borderRadius: "0px !important",
    },
  },
});

interface RoleAssignmentTreeProps {
  tagCollectionId: string;
  tags: RoleTag[];
  onPrimaryUserChange: (tagId: string, user: UserInformation | null) => void;
  onSecondaryUserChange: (tagId: string, user: UserInformation | null) => void;
  onRoleTitleChange: (tagId: string, roleTitle: string) => void;
  loading: boolean;
}

const RoleAssignmentTree = ({
  tags,
  onPrimaryUserChange,
  onSecondaryUserChange,
  onRoleTitleChange,
  loading,
}: RoleAssignmentTreeProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<string[]>([]);

  const {
    graphClient,
    isLoading: graphLoading,
    error: graphError,
  } = useGraphClient();

  const getUserId = (
    userInfo: UserInformation | null | undefined
  ): string | undefined => {
    if (!userInfo) return undefined;

    if (typeof userInfo === "string") return userInfo;
    return userInfo.id;
  };

  const initialSelectedUserIdsByTag = useMemo(() => {
    const tagUserIds: Record<
      string,
      { primary: string[]; secondary: string[] }
    > = {};
    tags.forEach((tag) => {
      const primaryUserId = getUserId(tag.assignedPrimaryUser);
      const secondaryUserId = getUserId(tag.assignedSecondaryUser);
      tagUserIds[tag.id] = {
        primary: primaryUserId ? [primaryUserId] : [],
        secondary: secondaryUserId ? [secondaryUserId] : [],
      };
    });
    return tagUserIds;
  }, [tags]);

  const tagIsOpen = useCallback(
    (id: string) => openItems.includes(id),
    [openItems]
  );

  const handleOpenChange = (
    _e: TreeOpenChangeEvent,
    data: TreeOpenChangeData
  ) => {
    const id = data.value as string;
    setOpenItems((prev) =>
      data.open ? [...prev, id] : prev.filter((i) => i !== id)
    );
  };
  const expandAll = () => setOpenItems(tags.map((t) => t.id));
  const collapseAll = () => setOpenItems([]);

  return (
    <div className={classes.container}>
      <div className={classes.controls}>
        <Button icon={<ArrowExpandAll20Regular />} onClick={expandAll}>
          {t("openAll")}
        </Button>
        <Button icon={<ArrowCollapseAll20Regular />} onClick={collapseAll}>
          {t("closeAll")}
        </Button>
      </div>
      <div className={classes.headerRow}>
        <span className={classes.headerTagSpace}></span>
        <span className={classes.headerLabel}>{t("roleTitle")}</span>
        <span className={classes.headerPickerLabel}>{t("primary")}</span>
        <span className={classes.headerPickerLabel}>{t("secondary")}</span>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <FlatTree
          openItems={openItems}
          onOpenChange={handleOpenChange}
          aria-label={t("rolesTree")}
          className={classes.tree}
        >
          {tags.map((tag) => {
            const ancestors = getAncestors(tag, [], tags);
            if (!ancestors.every((a) => tagIsOpen(a.id))) return null;

            return (
              <SimpleTreeItem
                key={tag.id}
                value={tag.id}
                open={tagIsOpen(tag.id)}
                itemType={tag.hasChildren ? "branch" : "leaf"}
                aria-level={tag.level ?? 1}
                aria-setsize={(tag.siblings ?? 1) + 1}
                aria-posinset={tag.position ?? 1}
                parentValue={tag.parentId}
                label={
                  <div className={classes.itemContent}>
                    <Tooltip content={tag.path} relationship="label">
                      <span className={classes.tagTitle}>{tag.title}</span>
                    </Tooltip>
                    {!tag.isTagCollection && (
                      <>
                        <div
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Input
                            value={tag.roleTitle || ""}
                            onChange={(e) =>
                              onRoleTitleChange(tag.id, e.target.value)
                            }
                            placeholder={t("roleTitle")}
                            style={{
                              minWidth: "300px",
                              maxWidth: "300px",
                              marginLeft: "16px",
                            }}
                          />
                        </div>
                        {graphError && (
                          <p>
                            {t("graphAuthenticationError")}: {graphError}
                          </p>
                        )}
                        {graphClient ? (
                          <div
                            className={classes.peoplePickerContainer}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                          >
                            <div className={classes.peoplePicker}>
                              <FluentPeoplePicker
                                graphClient={graphClient}
                                groupId={
                                  import.meta.env.VITE_USER_SEARCH_GROUP_ID
                                }
                                placeholder={t("searchName")}
                                multiSelect={false}
                                defaultSelectedUserIds={
                                  initialSelectedUserIdsByTag[tag.id]
                                    ?.primary || []
                                }
                                hideWhenSelected={true}
                                onChange={(users: UserInformation[]) => {
                                  if (users.length > 0) {
                                    onPrimaryUserChange(tag.id, users[0]);
                                  } else {
                                    onPrimaryUserChange(tag.id, null);
                                  }
                                }}
                              />
                            </div>
                            <div className={classes.peoplePicker}>
                              <FluentPeoplePicker
                                graphClient={graphClient}
                                groupId={
                                  import.meta.env.VITE_USER_SEARCH_GROUP_ID
                                }
                                placeholder={t("searchName")}
                                multiSelect={false}
                                defaultSelectedUserIds={
                                  initialSelectedUserIdsByTag[tag.id]
                                    ?.secondary || []
                                }
                                hideWhenSelected={true}
                                onChange={(users: UserInformation[]) => {
                                  if (users.length > 0) {
                                    onSecondaryUserChange(tag.id, users[0]);
                                  } else {
                                    onSecondaryUserChange(tag.id, null);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ) : graphLoading ? (
                          <Spinner size="tiny" />
                        ) : (
                          <p>{t("graphClientNotAvailable")}</p>
                        )}
                      </>
                    )}
                  </div>
                }
              />
            );
          })}
        </FlatTree>
      )}
    </div>
  );
};
export default RoleAssignmentTree;
